















import {
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  PropType,
  Ref,
  ref,
  watch
} from '~/utils/nuxt3-migration'
import { Geolocation } from '~/models/common/types'
import {
  CENTER_OF_GREECE,
  GR_CY_BG_BOUNDS,
  GREECE_BOUNDS
} from '~/constants/map'
import {
  geolocationsEqual,
  geolocationToGoogleGeolocation
} from '~/utils/geolocation'
import { GoogleMapControlPosition } from '~/constants/google/map'
import { useDep } from '~/compositions/dependency-container'
import { GeolocationService } from '~/services/navigator/GeolocationService'
import GoogleMapsService from '~/services/GoogleMapsService'
import { noop } from '~/utils/function'
import { sleep } from '~/utils/time'
import { useI18n } from '~/compositions/i18n'

export default defineComponent({
  props: {
    center: {
      type: Object as PropType<Geolocation>,
      default() {
        return CENTER_OF_GREECE
      }
    },
    zoom: {
      type: Number,
      default: 7
    },
    initialZoom: {
      type: Number,
      default: undefined
    },
    minZoom: {
      type: Number,
      default: 7
    },
    gestureHandling: {
      type: String,
      default: 'cooperative'
    },
    marker: {
      type: Object as PropType<Geolocation>,
      default: null
    },
    draggableMarker: {
      type: Boolean,
      default: false
    },
    bounds: {
      type: Object as PropType<google.maps.LatLngLiteral>,
      default() {
        return GR_CY_BG_BOUNDS
      }
    },
    showMyCurrentLocationButton: {
      type: Boolean,
      default: true
    },
    markerCircleRadius: {
      type: Number,
      default: null
    },
    radiusCircleOptions: {
      type: Object,
      default() {
        return {}
      }
    },
    heightClass: {
      type: String,
      default: 'tw-h-80'
    },
    hideMarker: {
      type: Boolean,
      default: false
    },
    mapClasses: {
      type: [String, Array],
      default: ''
    }
  },
  setup(props: any, { emit }: any) {
    const geolocationService = useDep(GeolocationService)
    const googleMapsService = useDep(GoogleMapsService)
    const { t } = useI18n()

    const mapTemplateRef = ref<HTMLElement | null>(null)
    const loading = ref<boolean>(true)
    const internalMarker = ref<Geolocation | null>(props.marker)
    const listeners: google.maps.MapsEventListener[] = []
    const mapMarker = ref<google.maps.marker.AdvancedMarkerElement | null>(null)
    const circle = ref<google.maps.Circle>()
    let map: google.maps.Map | null = null

    watch(
      () => props.zoom,
      newZoom => setZoom(newZoom)
    )

    watch(
      () => props.markerCircleRadius,
      async newCircle => {
        // Should probably adjust the map accordingly here instead of redrawing
        await createMap(mapTemplateRef, {
          center: props.marker || props.center,
          zoom: props.initialZoom || props.zoom,
          gestureHandling: props.gestureHandling,
          minZoom: props.minZoom,
          restriction: {
            latLngBounds: props.bounds,
            strictBounds: false
          }
        })

        await createMarker(props.marker, {
          draggable: props.draggableMarker,
          onMarkerPositionChange
        })

        await addCircleAroundMarker(
          newCircle,
          props.radiusCircleOptions,
          onCircleCenterChange,
          onCircleRadiusChange
        )
      }
    )

    watch(
      () => props.marker,
      newMarker => {
        // if the marker changed to nullish value or the marker geolocation is the same as before do not do anything
        if (
          !newMarker ||
          (internalMarker.value &&
            geolocationsEqual(newMarker, internalMarker.value))
        ) {
          return
        }
        internalMarker.value = newMarker
        moveMarker(newMarker)
      }
    )

    onMounted(async () => {
      if (!props.marker) {
        await nextTick()
      }

      await createMap(mapTemplateRef, {
        center: props.marker || props.center,
        zoom: props.initialZoom || props.zoom,
        gestureHandling: props.gestureHandling,
        minZoom: props.minZoom,
        restriction: {
          latLngBounds: props.bounds,
          strictBounds: false
        }
      })
      if (props.hideMarker && props.markerCircleRadius) {
        await addCircle({
          radius: props.markerCircleRadius,
          circleOptions: {
            center: geolocationToGoogleGeolocation(props.center),
            editable: false
          }
        })
      } else {
        await createMarker(props.marker, {
          draggable: props.draggableMarker,
          onMarkerPositionChange
        })
        if (props.markerCircleRadius) {
          await addCircleAroundMarker(
            props.markerCircleRadius,
            props.radiusCircleOptions,
            onCircleCenterChange,
            onCircleRadiusChange
          )
        }
      }

      if (props.showMyCurrentLocationButton) {
        addGpsLocationControlToMap(
          GoogleMapControlPosition.TOP_LEFT,
          geolocation => {
            moveMarker(geolocation)
            emit('marker-location-change', geolocation)
          }
        )
      }

      await addZoomChangeListener(onZoomChange)
      loading.value = false
    })

    onBeforeUnmount(async () => {
      await removeEventListeners()
    })

    function onMarkerPositionChange(location: Geolocation) {
      internalMarker.value = location
      emit('marker-location-change', location)
    }

    function onCircleCenterChange(location: Geolocation) {
      internalMarker.value = location
      emit('marker-location-change', location)
    }

    function onCircleRadiusChange(radius: number) {
      emit('circle-radius-change', radius)
    }

    function onZoomChange(zoom: number) {
      emit('zoom-change', zoom)
    }

    async function createMap(
      mapTemplateRef: Ref<HTMLElement | null>,
      options: any
    ): Promise<void> {
      const center =
        options.center?.lat && options.center?.lon
          ? options.center
          : CENTER_OF_GREECE
      const zoom = options.zoom || 12
      const minZoom = options.minZoom || 7
      const gestureHandling = options.gestureHandling || 'cooperative'
      const restriction = options.restriction || {
        latLngBounds: GREECE_BOUNDS,
        strictBounds: false
      }

      if (!mapTemplateRef.value) {
        throw new Error('Attempting to create a map without a map template ref')
      }
      map = await googleMapsService.createGoogleMap(mapTemplateRef.value, {
        center: geolocationToGoogleGeolocation(center),
        zoom,
        gestureHandling,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        restriction,
        minZoom,
        keyboardShortcuts: false
      })
    }

    async function addZoomChangeListener(onZoomChange: (zoom: number) => any) {
      if (!map) {
        return
      }

      listeners.push(
        await googleMapsService.addListener(map, 'zoom_changed', () => {
          if (!map) {
            return
          }
          const zoom = map.getZoom()
          if (zoom) {
            onZoomChange(zoom)
          }
        })
      )
    }

    async function createMarker(position: Geolocation, options: any) {
      const draggable = options.draggable || false
      const onMarkerPositionChange = options.onMarkerPositionChange || noop

      if (!map) {
        throw new Error('Attempting to create a marker on a non-existent map')
      }

      mapMarker.value = await googleMapsService.addMarkerToGoogleMap({
        map,
        position:
          (position && geolocationToGoogleGeolocation(position)) ||
          map.getCenter()
      })
      if (draggable) {
        await addMapMarkerListeners(onMarkerPositionChange)
      }
    }

    function moveMarkerToMapCenter() {
      const center = map!.getCenter()
      mapMarker.value!.position = center
      if (circle.value && center) {
        circle.value.setCenter(center)
      }
    }

    async function addMapMarkerListeners(onMarkerPositionChange: any = noop) {
      listeners.push(
        await googleMapsService.addListener(
          map!,
          'center_changed',
          async () => {
            if (!map || !mapMarker.value) {
              return
            }

            // This sleep is required to force the marker to move to the center of the map
            // after the zoom_changed event is triggered
            // TODO: Find a better way to handle this
            await sleep(50)
            moveMarkerToMapCenter()
          }
        )
      )

      listeners.push(
        await googleMapsService.addListener(map!, 'dragend', () => {
          if (!map || !mapMarker.value) {
            return
          }

          moveMarkerToMapCenter()

          const newMarkerPosition = mapMarker.value.position
          if (newMarkerPosition) {
            onMarkerPositionChange({
              lat: newMarkerPosition.lat,
              lon: newMarkerPosition.lng
            })
          }
        })
      )

      listeners.push(
        await googleMapsService.addListener(map!, 'zoom_changed', () => {
          if (!map || !mapMarker.value?.position) {
            return
          }

          const mapCenter = map.getCenter()
          const markerCenter = new google.maps.LatLng(mapMarker.value.position)

          if (mapCenter && mapCenter.equals(markerCenter)) {
            return
          }

          moveMarkerToMapCenter()

          const newMarkerPosition = mapMarker.value.position
          if (newMarkerPosition) {
            onMarkerPositionChange({
              lat: newMarkerPosition.lat,
              lon: newMarkerPosition.lng
            })
          }
        })
      )
    }

    async function removeEventListeners() {
      for (const listener of listeners) {
        if (!listener) {
          continue
        }

        await googleMapsService.removeListener(listener)
      }
    }

    function moveMarker(geolocation: Geolocation) {
      if (!map || !mapMarker.value) {
        return
      }
      const googleGeo = geolocationToGoogleGeolocation(geolocation)
      map.setCenter(googleGeo)
      mapMarker.value.position = googleGeo
    }

    function addGpsLocationControlToMap(
      position: GoogleMapControlPosition,
      onLocation: (geolocation: Geolocation) => void = noop
    ): void {
      if (!map) {
        return
      }

      const buttonWithText = document.createElement('div')
      buttonWithText.role = 'button'
      buttonWithText.style.backgroundColor = '#fff'
      buttonWithText.style.border = '2px solid #fff'
      buttonWithText.style.borderRadius = '3px'
      buttonWithText.style.boxShadow = 'rgb(0 0 0 / 30%) 0px 1px 4px -1px'
      buttonWithText.style.cursor = 'pointer'
      buttonWithText.style.margin = '8px'
      buttonWithText.style.textAlign = 'center'
      buttonWithText.title = t('current location') as string

      const textEl = document.createElement('div')
      textEl.style.color = 'rgb(25,25,25)'
      textEl.style.fontSize = '14px'
      textEl.style.lineHeight = '24px'
      textEl.style.paddingLeft = '5px'
      textEl.style.paddingRight = '5px'
      textEl.innerHTML = t('current location') as string

      buttonWithText.appendChild(textEl)
      buttonWithText.addEventListener('click', async () => {
        const gpsLocation = await geolocationService.getCurrentGpsLocationWithSnackbarOnError()
        if (gpsLocation) {
          onLocation(gpsLocation)
        }
      })

      map.controls[position].push(buttonWithText)
    }

    function setZoom(zoom: number) {
      if (!map) {
        return
      }
      // Using next tick here to avoid wrong map/marker position due to premature zooming
      nextTick(() => map?.setZoom(zoom))
    }

    async function addCircle({
      radius,
      circleOptions = { editable: false }
    }: {
      radius: number
      circleOptions: google.maps.CircleOptions
    }) {
      if (!map) {
        return
      }

      await googleMapsService.addCircleToGoogleMap(map, radius, circleOptions)
    }

    async function addCircleAroundMarker(
      radius: number,
      circleOptions = { editable: false },
      onCircleCenterChange: (center: Geolocation) => any = noop,
      onCircleRadiusChange: (radius: number) => any = noop
    ) {
      if (!map || !mapMarker.value) {
        return
      }

      circle.value = await googleMapsService.addCircleToGoogleMap(map, radius, {
        ...circleOptions,
        center: mapMarker.value.position
      })

      const bounds = circle.value.getBounds()

      if (bounds) {
        map.fitBounds(bounds)
      }

      listeners.push(
        await googleMapsService.addListener(
          circle.value,
          'center_changed',
          () => {
            // This event is triggered multiple times consecutively when dragging the bound marker
            const center = circle.value!.getCenter()
            if (center) {
              if (mapMarker.value) {
                mapMarker.value.position = center
              }
              onCircleCenterChange({ lat: center.lat(), lon: center.lng() })
            }
          }
        )
      )

      listeners.push(
        await googleMapsService.addListener(
          circle.value,
          'radius_changed',
          () => onCircleRadiusChange(circle.value!.getRadius())
        )
      )
    }

    return { mapTemplateRef, loading, setZoom }
  }
})
